/**
 * Public Setup Guide — /setup-in-15-minutes
 * ==========================================
 * The HN-launch trust hook. Renders the public setup-guide markdown from
 * /api/setup-guide/current. Anonymous, no auth.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, Clock, ExternalLink, Loader2, ShieldCheck,
  Zap, BookOpen,
} from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetupIn15Minutes = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/setup-guide/current`);
        setData(data);
      } catch (e) {
        setErr(e?.message || 'Could not load guide');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="Onboard in 15 minutes · CARTIEAI"
        description="The literal checklist our first paying pilots run on day one. Free tier on every key. No demo call. No sales gating."
        url="https://cartieai.com/setup-in-15-minutes"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white" data-testid="setup-guide-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to CARTIE
          </Link>
        </div>

        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge className="bg-emerald-500/20 text-emerald-300">
              <Clock className="w-3 h-3 mr-1 inline" /> ~15 minutes
            </Badge>
            <Badge className="bg-emerald-500/20 text-emerald-300">
              <ShieldCheck className="w-3 h-3 mr-1 inline" /> No payment required
            </Badge>
            <Badge className="bg-amber-500/20 text-amber-200">
              <Zap className="w-3 h-3 mr-1 inline" /> Self-serve
            </Badge>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="setup-guide-title"><BrandTitle>Onboard in 15 minutes</BrandTitle></h1>
          <p className="text-base sm:text-lg text-slate-200 max-w-3xl leading-relaxed">
            Most FinOps tools hide their onboarding behind <em>"talk to sales"</em>.
            We publish ours because we want you to run the platform before you talk to us — not after.
          </p>
        </div>

        {loading && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-6 text-sm text-slate-200 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading guide…
            </CardContent>
          </Card>
        )}

        {err && (
          <Card className="border-rose-500/30 bg-rose-500/10">
            <CardContent className="pt-6 text-sm text-rose-200">{err}</CardContent>
          </Card>
        )}

        {data && (
          <Card className="border-emerald-500/20">
            <CardContent className="pt-6">
              <article
                className="prose prose-slate max-w-none prose-headings:font-semibold prose-h1:text-3xl prose-h2:text-xl prose-h2:mt-8 prose-h2:border-b prose-h2:border-emerald-500/20 prose-h2:pb-2 prose-code:text-emerald-300 prose-code:bg-slate-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-slate-900 prose-pre:text-slate-200 prose-a:text-emerald-300 prose-strong:text-white"
                data-testid="setup-guide-content"
              >
                <ReactMarkdown
                  components={{
                    a: ({ href, children, ...props }) => (
                      <a
                        href={href}
                        target={href?.startsWith('http') ? '_blank' : undefined}
                        rel={href?.startsWith('http') ? 'noreferrer' : undefined}
                        {...props}
                      >
                        {children}
                        {href?.startsWith('http') && <ExternalLink className="w-3 h-3 inline-block ml-0.5" />}
                      </a>
                    ),
                  }}
                >
                  {data.content}
                </ReactMarkdown>
              </article>
            </CardContent>
          </Card>
        )}

        <Card className="border-emerald-500/30 bg-emerald-500/10 mt-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <BookOpen className="w-6 h-6 text-emerald-300 shrink-0" />
              <div>
                <div className="font-semibold text-emerald-200 mb-1">
                  Ready to start?
                </div>
                <div className="text-sm text-emerald-300 mb-3">
                  Sign up below — no credit card. We'll send you a magic link.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to="/signup">
                    <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="setup-guide-signup-btn">
                      Sign up · free audit
                    </Button>
                  </Link>
                  <Link to="/roi-pledge">
                    <Button variant="outline" className="border-emerald-300 text-emerald-300" data-testid="setup-guide-pledge-btn">
                      Read the ROI Pledge first
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {data && (
          <div className="mt-4 text-xs text-slate-200 text-center">
            Version {data.version} · {data.size_bytes} bytes · {data.issued_at}
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupIn15Minutes;
